import React, { useState, useCallback, useRef, useEffect } from "react";
import { BarcodeScanner, BarcodeFormat } from "react-barcode-scanner";
import "react-barcode-scanner/polyfill";
import { getBarcodeProductsService } from "../Services/ApiServices";
import toast from "react-hot-toast";
import type { ScanOptions } from "react-barcode-scanner";
import Styles from "./BarcodeScannerModal.module.css";
import Loader from "../SharedComponents/Loader/Loader";
import { AxiosResponse } from "axios";

interface ProductImageDataProps {
  code: string;
  id: number;
  image: string[];
  name: string;
  video: any[];
}

interface ProductUnitDataProps {
  id: number;
  unit_value: number;
  unit_id: string;
  selling_price: number;
  quantity: number;
  min_quantity: number;
  mrp_price: number;
  franchise_price: number;
  franchise_customer_price: number;
  barcode: string;
}
interface ProductDataProps {
  list_type: number;
  product_id: number;
  kstore: number; // 0 or 1
  product_code: string;
  product_name: string;
  brand_id: number;
  brand_name: string;
  product_category_id: number;
  product_category_name: string;
  product_subcategory_id: number;
  product_subcategory_name: string;
  product_image: string;
  wish_list: number; // 0 or 1
  quantity: number;
  min_quantity: number;
  max_quantity: number;
  selling_price: number;
  mrp_price: number;
  franchise_customer_price: number;
  franchise_price: number;
  customer_price_on_franchise: number;
  selectedUnit?: ProductUnitDataProps;
  colorItem?: ProductImageDataProps | null;
  product_unit: ProductUnitDataProps[];
  product_images: ProductImageDataProps[];
  barcode: string;
}

interface BarcodeProductResponseProps {
  items: ProductDataProps[];
  total: number;
  page: number;
  size: number;
}

interface BarcodeScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  isEnableGetProductDetails: boolean;
  onScanBarcode: (barcode: string) => void;
  onFetchProduct: (product: ProductDataProps) => void;
}
const SCAN_OPTIONS: ScanOptions = {
  delay: 100,
  formats: Object.values(BarcodeFormat),
};
function BarcodeScannerModal({
  isOpen,
  onClose,
  isEnableGetProductDetails = false,
  onScanBarcode,
  onFetchProduct,
}: BarcodeScannerModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  // shouldRender stays true briefly after isOpen→false so the BarcodeScanner
  // can finish any in-flight detect() call before unmounting.
  const [shouldRender, setShouldRender] = useState(false);
  // Incrementing this key remounts BarcodeScanner fresh (paused=false from
  // birth) instead of toggling paused true→false, which causes a race between
  // startScan() and video.play() leading to InvalidStateError on detect().
  const [scannerKey, setScannerKey] = useState(0);
  const hasScannedRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      hasScannedRef.current = false;
      setIsPaused(false);
      setCameraError(null);
      setIsLoading(false);

      if (
        typeof window !== "undefined" &&
        !navigator.mediaDevices?.getUserMedia
      ) {
        setCameraError(
          "Camera is not available. Please open the app over HTTPS to use the barcode scanner.",
        );
        return;
      }
    } else {
      // Pause the scanner immediately so no new detect() calls are queued,
      // then unmount after 300 ms once any pending detect() promise resolves.
      setIsPaused(true);
      const timer = setTimeout(() => setShouldRender(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleCapture = useCallback(
    (barcodes: { rawValue: string }[]) => {
      if (hasScannedRef.current || barcodes.length === 0) return;

      const barcode = barcodes[0].rawValue;
      if (!barcode) return;

      hasScannedRef.current = true;
      setIsPaused(true);

      if (isEnableGetProductDetails) {
        setIsLoading(true);

        const str = sessionStorage.getItem("customerlog");
        const parsedCustomerData = JSON.parse(str || "{}");

        const formData = new FormData();
        formData.append("barcode", barcode);
        if (parsedCustomerData?.id) {
          formData.append("franchise_id", parsedCustomerData.id);
        }

        getBarcodeProductsService(formData)
          .then((res: AxiosResponse<BarcodeProductResponseProps>) => {
            if (res.data.items[0]) {
              const product = res.data.items[0];

              let selectedColorItem = product.product_images?.find(
                (ele: any) => ele.isActive,
              );

              const selectedUnit = product.product_unit.find(
                (item) => item.barcode === product.barcode,
              );

              onFetchProduct?.({
                ...product,
                colorItem: selectedColorItem,
                selectedUnit,
              });
              onClose();
            } else {
              toast.dismissAll();
              toast.error("Product not found");
              hasScannedRef.current = false;
              setIsPaused(false);
              setScannerKey((k) => k + 1);
            }
          })
          .catch((err: any) => {
            const errMsg =
              err?.response?.data?.detail?.[0]?.msg ||
              err.message ||
              "Failed to fetch product";
            toast.error(errMsg);
            hasScannedRef.current = false;
            setIsPaused(false);
            setScannerKey((k) => k + 1);
          })
          .finally(() => {
            setIsLoading(false);
          });
      } else {
        onScanBarcode?.(barcode);
        onClose();
      }
    },
    [isEnableGetProductDetails, onFetchProduct, onScanBarcode, onClose],
  );

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  if (!shouldRender) return null;

  return (
    <div
      className={Styles.overlay}
      style={{ display: isOpen ? undefined : "none" }}
      onClick={handleOverlayClick}
    >
      <div
        className={Styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label="Barcode Scanner"
      >
        <div className={Styles.header}>
          <h5 className={Styles.title}>Scan Barcode</h5>
          <button
            className={Styles.closeBtn}
            onClick={onClose}
            aria-label="Close scanner"
            disabled={isLoading}
          >
            &times;
          </button>
        </div>

        <div className={Styles.body}>
          {cameraError ? (
            <div className={Styles.errorState}>
              <span className={Styles.errorIcon}>&#9888;</span>
              <p className={Styles.errorText}>{cameraError}</p>
            </div>
          ) : (
            <div className={Styles.scannerWrapper}>
              <BarcodeScanner
                key={scannerKey}
                onCapture={handleCapture}
                paused={isPaused}
                options={SCAN_OPTIONS}
                trackConstraints={{ facingMode: { ideal: "environment" } }}
                className={Styles.scanner}
              />
              {!isPaused && <div className={Styles.scanLine} />}
              <div className={Styles.scanFrame}>
                <span className={Styles.cornerTL} />
                <span className={Styles.cornerTR} />
                <span className={Styles.cornerBL} />
                <span className={Styles.cornerBR} />
              </div>
            </div>
          )}
        </div>

        <div className={Styles.footer}>
          <p className={Styles.hint}>
            {isLoading
              ? "Fetching product details..."
              : "Point your camera at the barcode to scan"}
          </p>
        </div>
      </div>

      <Loader isLoader={isLoading} />
    </div>
  );
}

export default BarcodeScannerModal;
